import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveUrl, formatApiError } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";
import { Star, Upload, Check, X } from "lucide-react";

export default function ReviewSubmit() {
  const { token } = useParams();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/reviews/by-token/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const fd = new FormData();
      fd.append("rating", String(rating));
      fd.append("comment", comment);
      files.forEach((f) => fd.append("files", f));
      await api.post(`/reviews/by-token/${token}`, fd);
      setSuccess(true);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (error && !data) {
    return <div className="min-h-screen flex items-center justify-center p-6"><div className="card-surface p-10 max-w-md text-center"><h2 className="font-serif text-2xl mb-3" style={{color:"var(--brand-primary)"}}>Link invalid</h2><p className="text-sm" style={{color:"var(--brand-text-secondary)"}}>{error}</p><Link to="/" className="btn-gold mt-6 inline-block">Acasa</Link></div></div>;
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm" style={{color:"var(--brand-text-secondary)"}}>Se incarca...</div>;
  if (data.already_submitted) {
    return <div className="min-h-screen flex items-center justify-center p-6"><div className="card-surface p-10 max-w-md text-center"><Check className="w-12 h-12 mx-auto mb-4" style={{color:"var(--brand-primary)"}} /><h2 className="font-serif text-2xl mb-3">Multumim!</h2><p className="text-sm" style={{color:"var(--brand-text-secondary)"}}>Recenzia ta a fost deja inregistrata.</p><Link to="/" className="btn-gold mt-6 inline-block">Vezi colectia</Link></div></div>;
  }

  if (success) {
    return <div className="min-h-screen flex items-center justify-center p-6"><div className="card-surface p-10 max-w-md text-center" data-testid="review-success"><Check className="w-12 h-12 mx-auto mb-4" style={{color:"var(--brand-primary)"}} /><h2 className="font-serif text-3xl mb-3">Recenzie trimisa!</h2><p className="text-sm mb-6" style={{color:"var(--brand-text-secondary)"}}>Iti multumim pentru feedback. Recenzia va aparea pe site dupa moderare.</p><Link to="/" className="btn-gold inline-block">Inapoi la site</Link></div></div>;
  }

  const car = data.car || {};
  const res = data.reservation || {};
  const thumb = car.images?.[0]?.url ? resolveUrl(car.images[0].url) : null;

  return (
    <div className="min-h-screen py-12 px-6" data-testid="review-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <span className="font-serif text-xl" style={{color:"var(--brand-primary)"}}>{settings?.brand_name || "Inchirieri Auto Pascani"}</span>
          <h1 className="font-serif text-4xl mt-4">Lasa o recenzie</h1>
        </div>

        <div className="card-surface p-8 mb-6 flex items-center gap-4">
          {thumb && <img src={thumb} alt="" className="w-24 h-20 object-cover" />}
          <div>
            <div className="label-eyebrow mb-1">{car.brand}</div>
            <h2 className="font-serif text-xl">{car.name}</h2>
            <p className="text-xs mt-1" style={{color:"var(--brand-text-secondary)"}}>{res.start_date} → {res.end_date}</p>
          </div>
        </div>

        <form onSubmit={submit} className="card-surface p-8 space-y-6">
          <div>
            <label className="label-eyebrow block mb-3">Rating</label>
            <div className="flex gap-1" data-testid="rating-stars">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`}>
                  <Star className="w-8 h-8" fill={n <= rating ? "var(--brand-primary)" : "none"} stroke="var(--brand-primary)" />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-3">Comentariu</label>
            <textarea rows={5} className="brand-input" placeholder="Cum a fost experienta cu masina?" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
          </div>
          <div>
            <label className="label-eyebrow block mb-3">Adauga poze sau un video scurt (optional, max 6 fisiere)</label>
            <label className="btn-ghost cursor-pointer flex items-center justify-center gap-2 py-6">
              <Upload className="w-4 h-4" /> Selecteaza fisiere
              <input type="file" multiple accept="image/*,video/*" className="hidden" onChange={(e) => setFiles(Array.from(e.target.files).slice(0, 6))} data-testid="review-files" />
            </label>
            {files.length > 0 && (
              <p className="text-xs mt-2" style={{color:"var(--brand-text-secondary)"}}>{files.length} fisiere selectate</p>
            )}
          </div>
          {error && <div className="text-sm" style={{color:"var(--brand-primary)"}}>{error}</div>}
          <button type="submit" disabled={submitting} className="btn-gold-filled w-full" data-testid="submit-review">
            {submitting ? "Se trimite..." : "Trimite recenzia"}
          </button>
        </form>
      </div>
    </div>
  );
}
